/**
 * 世界书系统 Store
 * 实现世界书的 CRUD 操作、关键词触发检测
 */

import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { useIndexedDBStorage, waitForStorage } from '../utils/indexed-db-storage'

export interface WorldBook {
  id: string
  name: string
  content: string
  triggerType: 'always' | 'keyword'
  keywords: string[]
  createdAt: number
  updatedAt: number
}

export const useWorldBookStore = defineStore('worldbook', () => {
  // ============ 状态管理 ============

  /** ✨ 世界书列表 - 迁移到 IndexedDB（世界书内容可能很大） */
  const worldBooksStorage = useIndexedDBStorage<WorldBook[]>('worldbook/list', [], {
    debounceMs: 300,
  })
  const worldBooks = worldBooksStorage.data

  // 等待存储加载完成
  const worldbookStorageLoaded = ref(false)
  waitForStorage(worldBooksStorage).then(() => {
    worldbookStorageLoaded.value = true
    console.debug('✅ 世界书存储加载完成（IndexedDB）')
  })

  // ============ 计算属性 ============

  /** 始终启用的世界书（核心设定） */
  const alwaysActiveBooks = computed(() =>
    worldBooks.value.filter(book => book.triggerType === 'always'),
  )

  /** 关键词触发的世界书（背景知识） */
  const keywordBooks = computed(() =>
    worldBooks.value.filter(book => book.triggerType === 'keyword'),
  )

  /** 按更新时间排序的世界书 */
  const sortedByUpdateTime = computed(() =>
    [...worldBooks.value].sort((a, b) => b.updatedAt - a.updatedAt),
  )

  /** 按创建时间排序的世界书 */
  const sortedByCreateTime = computed(() =>
    [...worldBooks.value].sort((a, b) => b.createdAt - a.createdAt),
  )

  /** 按名称排序的世界书（升序） */
  const sortedByNameAsc = computed(() =>
    [...worldBooks.value].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
  )

  /** 按名称排序的世界书（降序） */
  const sortedByNameDesc = computed(() =>
    [...worldBooks.value].sort((a, b) => (b.name || '').localeCompare(a.name || '')),
  )

  // ============ 核心功能：CRUD 操作 ============

  /**
   * 获取指定 ID 的世界书
   */
  function getWorldBook(id: string): WorldBook | undefined {
    return worldBooks.value.find(book => book.id === id)
  }

  /**
   * 创建新世界书
   */
  function createWorldBook(
    book: Omit<WorldBook, 'id' | 'createdAt' | 'updatedAt'>,
  ): WorldBook {
    const newBook: WorldBook = {
      ...book,
      id: `wb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now(),
      updatedAt: Date.now(),
    }

    // 添加到列表
    worldBooks.value.push(newBook)

    // 软上限警告（不阻止创建，但提醒用户）
    if (worldBooks.value.length > 500) {
      console.warn(`📚 世界书数量已达 ${worldBooks.value.length}，过多可能影响性能`)
    }

    console.info('📚 创建新世界书:', newBook.name)
    return newBook
  }

  /**
   * 更新世界书
   */
  function updateWorldBook(
    id: string,
    updates: Partial<Omit<WorldBook, 'id' | 'createdAt'>>,
  ): boolean {
    const index = worldBooks.value.findIndex(book => book.id === id)
    if (index === -1) {
      console.warn('世界书不存在:', id)
      return false
    }

    worldBooks.value[index] = {
      ...worldBooks.value[index],
      ...updates,
      updatedAt: Date.now(),
    }

    console.info('📝 更新世界书:', worldBooks.value[index].name)
    return true
  }

  /**
   * 删除世界书
   */
  function deleteWorldBook(id: string): boolean {
    const index = worldBooks.value.findIndex(book => book.id === id)
    if (index === -1) {
      console.warn('世界书不存在:', id)
      return false
    }

    const deletedBook = worldBooks.value[index]
    worldBooks.value.splice(index, 1)

    console.info('🗑️ 删除世界书:', deletedBook.name)
    return true
  }

  /**
   * 批量删除世界书
   */
  function deleteWorldBooks(ids: string[]): number {
    const deletedCount = ids.filter(id => deleteWorldBook(id)).length
    console.info(`🗑️ 批量删除 ${deletedCount} 本世界书`)
    return deletedCount
  }

  /**
   * 清空所有世界书
   */
  function clearAllWorldBooks(): void {
    const count = worldBooks.value.length
    worldBooks.value = []
    console.info(`🗑️ 清空所有世界书 (共 ${count} 本)`)
  }

  // ============ 核心功能：关键词检测 ============

  /**
   * 检测消息中是否包含世界书的关键词
   * 返回匹配到的世界书列表
   */
  function detectTriggeredBooks(message: string): WorldBook[] {
    const messageLower = message.toLowerCase()
    const triggeredBooks: WorldBook[] = []

    for (const book of keywordBooks.value) {
      // 检查是否有任何关键词匹配
      const hasMatch = book.keywords.some(keyword =>
        messageLower.includes(keyword.toLowerCase()),
      )

      if (hasMatch) {
        triggeredBooks.push(book)
      }
    }

    if (triggeredBooks.length > 0) {
      console.info(
        `🔍 检测到 ${triggeredBooks.length} 本相关世界书:`,
        triggeredBooks.map(b => b.name).join(', '),
      )
    }

    return triggeredBooks
  }

  /**
   * 获取应该注入到对话的世界书内容
   * 包括：始终启用的世界书 + 关键词触发的世界书
   */
  function getRelevantWorldBooks(message?: string): WorldBook[] {
    const relevantBooks = [...alwaysActiveBooks.value]

    // 如果提供了消息，检测关键词触发
    if (message) {
      const triggeredBooks = detectTriggeredBooks(message)
      relevantBooks.push(...triggeredBooks)
    }

    return relevantBooks
  }

  /**
   * 将世界书内容格式化为字符串，用于注入到 prompt
   */
  function formatWorldBooksForPrompt(books: WorldBook[]): string {
    if (books.length === 0)
      return ''

    const sections = books.map((book) => {
      const header = book.triggerType === 'always' ? '[核心设定]' : '[背景知识]'
      return `${header} ${book.name}:\n${book.content}`
    })

    return sections.join('\n\n')
  }

  /**
   * 搜索世界书（根据名称、内容或关键词）
   */
  function searchWorldBooks(query: string): WorldBook[] {
    if (!query.trim())
      return worldBooks.value

    const queryLower = query.toLowerCase()
    return worldBooks.value.filter(
      book =>
        book.name.toLowerCase().includes(queryLower)
        || book.content.toLowerCase().includes(queryLower)
        || book.keywords.some(k => k.toLowerCase().includes(queryLower)),
    )
  }

  // ============ 统计信息 ============

  /**
   * 获取世界书统计信息
   */
  const stats = computed(() => ({
    total: worldBooks.value.length,
    alwaysActive: alwaysActiveBooks.value.length,
    keywordBased: keywordBooks.value.length,
    totalKeywords: keywordBooks.value.reduce(
      (sum, book) => sum + book.keywords.length,
      0,
    ),
  }))

  return {
    // 状态
    worldBooks,
    worldbookStorageLoaded,

    // 计算属性
    alwaysActiveBooks,
    keywordBooks,
    sortedByUpdateTime,
    sortedByCreateTime,
    sortedByNameAsc,
    sortedByNameDesc,
    stats,

    // 方法
    getWorldBook,
    createWorldBook,
    updateWorldBook,
    deleteWorldBook,
    deleteWorldBooks,
    clearAllWorldBooks,
    detectTriggeredBooks,
    getRelevantWorldBooks,
    formatWorldBooksForPrompt,
    searchWorldBooks,
  }
})
